// ============================================================
// SALT — Game Context
// Detects which game the user is on via subdomain parsing.
// Fetches season header automatically when a game is detected.
// Provides game identity + season to all components.
// ============================================================

import React, { createContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axiosClient from '../api/axiosClient';
import type { SeasonHeader } from '../types/dashboard.types';

// ── Game Code → ID Mapping ─────────────────────────────────

const GAME_CODE_MAP: Record<string, number> = {
  hi: 1, // Halo Infinite
  h3: 2, // Halo 3
};

const GAME_NAME_MAP: Record<string, string> = {
  hi: 'Halo Infinite',
  h3: 'Halo 3',
};

// ── Context Shape ──────────────────────────────────────────

export interface GameContextValue {
  gameId: number | null;
  gameCode: string | null;
  gameName: string | null;
  season: SeasonHeader | null;
  isLoading: boolean;
  hasGame: boolean;
  error: string | null;
  setSeason: (season: SeasonHeader | null) => void;
}

export const GameContext = createContext<GameContextValue>({
  gameId: null,
  gameCode: null,
  gameName: null,
  season: null,
  isLoading: true,
  hasGame: false,
  error: null,
  setSeason: () => {},
});

// ── Subdomain Parsing ──────────────────────────────────────

function parseGameCode(hostname: string): string | null {
  const lower = hostname.toLowerCase();

  // Production: {code}.playsalt.gg
  const prodMatch = lower.match(/^([a-z0-9]+)\.playsalt\.gg$/);
  if (prodMatch) {
    const code = prodMatch[1];
    return code !== 'www' && GAME_CODE_MAP[code] !== undefined ? code : null;
  }

  // Local dev: {code}.localhost or {code}.localhost:PORT
  const localMatch = lower.match(/^([a-z0-9]+)\.localhost(:\d+)?$/);
  if (localMatch) {
    const code = localMatch[1];
    return GAME_CODE_MAP[code] !== undefined ? code : null;
  }

  return null;
}

function getGameCodeFromQueryParam(): string | null {
  const params = new URLSearchParams(window.location.search);
  const gameIdParam = params.get('game_id');
  if (!gameIdParam) return null;

  const id = parseInt(gameIdParam, 10);
  const entry = Object.entries(GAME_CODE_MAP).find(([, v]) => v === id);
  return entry ? entry[0] : null;
}

// ── Provider ───────────────────────────────────────────────

interface GameProviderProps {
  children: ReactNode;
}

export function GameProvider({ children }: GameProviderProps) {
  const [season, setSeasonState] = useState<SeasonHeader | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const gameCode = parseGameCode(window.location.hostname) || getGameCodeFromQueryParam();
  const gameId = gameCode ? GAME_CODE_MAP[gameCode] ?? null : null;
  const gameName = gameCode ? GAME_NAME_MAP[gameCode] ?? null : null;
  const hasGame = gameId !== null;

  // Allow pages to override season data (e.g. dashboard gets it from its own API call)
  const setSeason = useCallback((s: SeasonHeader | null) => {
    setSeasonState(s);
  }, []);

  // Fetch season header when a game is detected
  useEffect(() => {
    if (!gameId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const fetchSeason = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const res = await axiosClient.get(`/seasons/current?game_id=${gameId}`);
        if (cancelled) return;
        if (res.data?.success && res.data.data) {
          setSeasonState(res.data.data);
        }
      } catch {
        if (cancelled) return;
        // Non-critical — season might not exist yet
        setError(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchSeason();
    return () => { cancelled = true; };
  }, [gameId]);

  const value: GameContextValue = {
    gameId,
    gameCode,
    gameName,
    season,
    isLoading,
    hasGame,
    error,
    setSeason,
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

export default GameContext;